/**
 * Composing <Route> in React Router v6
 * https://gist.github.com/mjackson/d54b40a094277b7afdd6b81f51a0393f
 *
 * Upgrading from v5
 * https://reactrouter.com/docs/en/v6/upgrading/v5
 */
import { lazy, Suspense } from 'react';
import { Routes, Route, redirect, Navigate, useNavigate } from 'react-router-dom';

import { Dashboard } from 'pages/Dashboard';
import { Login } from 'pages/Login';
import { NotFound } from 'pages/NotFound';
import { PrivateRoute } from './PrivateRoute';
import PrivateLayout from './PrivateLayout';
import { PublicRoute } from './PublicRoute';
// import { Shop } from 'pages/Shop';
import ShopDetail from 'pages/Shop/Detail';
import Reconhecer from 'pages/Reconhecer';
import Extrato from 'pages/Extrato';
import Historico from 'pages/Historico';
import Faq from 'pages/Faq';
import Regulamento from 'pages/Regulamento';
import Convidar from 'pages/Convidar';
// import { ShopTeaser } from 'pages/Shop/teaser';
import { ApreciacaoFlowGate } from 'pages/MinhaEquipe/ApreciarRealizacao';
import { FluxoNormal } from 'pages/MinhaEquipe/ApreciarRealizacao/normal';
import { FluxoMAD } from 'pages/MinhaEquipe/ApreciarRealizacao/mad';
import { Catalog } from 'pages/Catalog';
import MissaoDestaqueCreate from 'pages/MissaoDestaque/Create';
import RealizacaoDestaque from 'pages/RealizacaoDestaque';
import GaleriaReconhecimento from 'pages/GaleriaReconhecimento';
import DestaquesCultura from 'pages/DestaquesCultura';
import { DestaquesCulturaDetail } from 'pages/DestaquesCultura/detail';
import { Manifesto } from 'pages/Manifesto';
import MinhaEquipe from 'pages/MinhaEquipe';
import CustomNavigationClient from 'sso/azure/CustomNavigationClient';
import { MsalProvider } from '@azure/msal-react';
import Rede from 'pages/Rede';
import RealizacoesDoPrefixo from 'pages/Rede/RealizacoesDoPrefixo';
import ReconhecimentosDoPrefixo from 'pages/Rede/ReconhecimentosDoPrefixo';
import { ROTA_DAS_REALIZACOES_DO_PREFIXO, ROTA_DOS_RECONHECIMENTOS_DO_PREFIXO } from 'pages/Rede/rotas-do-feed';
import { Votacao } from 'pages/Votacao';
import Catalogo from 'pages/Catalogo';
import Destaque from 'pages/Destaque';
// import AnalisarParticipacaoGerentes from 'pages/MinhaEquipe/AnalisarParticipacaoGerentes';
import AnalisarParticipacaoEquipe from 'pages/MinhaEquipe/AnalisarParticipacaoEquipe';
import Challenges from 'pages/Challenges';
import Selos from 'pages/Selos';
import ChallengesLancamento from 'pages/ChallengesLancamento';
import { Shop } from 'pages/Shop';
import { Store } from 'pages/Store';
import PontuamMasNaoAvaliam from 'pages/MinhaEquipe/PontuamMasNaoAvaliam';
import AcoesEcoa from 'pages/AcoesEcoa';
import GuardaDoIntegranteEcoa from 'pages/AcoesEcoa/GuardaDoIntegranteEcoa';
import AcaoCreate from 'pages/Acao/Create';
import { ApreciarAcao } from 'pages/ApreciarAcao';
import { Preference } from 'pages/Preference';
import TesteSharePoint from 'pages/TesteSharepoint';
import ReconhecerJunino from 'pages/ReconhecerJunino';
import WheelItem from 'components/wheel';
import ScratchGame from 'components/scratch-game';
import ExperienceCatalog from 'pages/ExperienceCatalog';
import ExperienceCatalogFeedbackPage from 'pages/ExperienceCatalog/feedbackPage';
import { rotasDasVersoesAntigas } from './rotas-das-versoes-antigas';

// Vitrine dos componentes da nova identidade, só em desenvolvimento. A condição
// fica no nível do módulo: no build de produção ela vira `false` e o webpack
// descarta o import(), então nem a rota nem o código da página entram no bundle.
const VitrineComponentes =
  process.env.NODE_ENV === 'development' ? lazy(() => import('pages/VitrineComponentes')) : null;


export const RouteList = ({ msalInstance }: any) => {
  const navigate = useNavigate();
  const navigationClient = new CustomNavigationClient(navigate);
  msalInstance.setNavigationClient(navigationClient);

  return (
    <MsalProvider instance={msalInstance}>
      <Routes>
        {/* CAN-02: os endereços das versões antigas (/v2/*, /v3/*) levam às rotas da nova identidade, que atendem
            todos os ciclos; o ciclo do app não muda. */}
        {rotasDasVersoesAntigas()}

        {/* Rotas privadas sob uma casca única: menu, conteúdo e modais globais, para todos os ciclos. */}
        <Route element={<PrivateLayout redirectTo="/login" />}>
        <Route
          path="/"
          element={
            <PrivateRoute redirectTo="/login">
              <Dashboard />
            </PrivateRoute>
          }
        >
          <Route
            path="/reconhecer"
            element={
              <PrivateRoute redirectTo="/login">
                <Reconhecer />
              </PrivateRoute>
            }
          />

          <Route
            path="/reconhecer/:id"
            element={
              <PrivateRoute redirectTo="/login">
                <Reconhecer />
              </PrivateRoute>
            }
          />

          <Route
            path="/convidar"
            element={
              <PrivateRoute redirectTo="/login">
                <Convidar />
              </PrivateRoute>
            }
          />
        </Route>

        <Route path="/missao-destaque" element={<Navigate to="/realizacao-destaque" />} />

        <Route
          path="/realizacao-destaque"
          element={
            <PrivateRoute redirectTo="/login">
              <RealizacaoDestaque />
            </PrivateRoute>
          }
        >
          <Route
            path="/realizacao-destaque/:id"
            element={
              <PrivateRoute redirectTo="/login">
                <MissaoDestaqueCreate />
              </PrivateRoute>
            }
          />
        </Route>

        {/* AJ3-15: a lista do prefixo passou para o Feed Joga Junto. */}
        <Route path="/realizacoes-do-prefixo" element={<Navigate to={ROTA_DAS_REALIZACOES_DO_PREFIXO} replace />} />

        <Route
          path="/acao-ecoa"
          element={
            <PrivateRoute redirectTo="/login">
              <GuardaDoIntegranteEcoa>
                <AcoesEcoa />
              </GuardaDoIntegranteEcoa>
            </PrivateRoute>
          }
        >
          <Route
            path="/acao-ecoa/:id"
            element={
              <PrivateRoute redirectTo="/login">
                <AcaoCreate />
              </PrivateRoute>
            }
          />
        </Route>

        <Route
          path="/validar-acao-ecoa"
          element={
            <PrivateRoute redirectTo="/login">
              <ApreciarAcao />
            </PrivateRoute>
          }
        >
          <Route
            path="/validar-acao-ecoa/:id"
            element={
              <PrivateRoute redirectTo="/login">
                <AcaoCreate />
              </PrivateRoute>
            }
          />
        </Route>

        <Route path="/validar-destaque" element={<Navigate to="/minha-equipe/validar-apreciar-realizacao" />} />

        <Route
          path="/extrato"
          element={
            <PrivateRoute redirectTo="/login">
              <Extrato />
            </PrivateRoute>
          }
        />

        <Route path="/quadro-equipe" element={<Navigate to="/minha-equipe" />} />

        <Route path="/minha-equipe">
          <Route
            index
            element={
              <PrivateRoute redirectTo="/login">
                <MinhaEquipe />
              </PrivateRoute>
            }
          />

          <Route path="validar-apreciar-realizacao">
            <Route
              index
              element={
                <PrivateRoute redirectTo="/login">
                  <ApreciacaoFlowGate />
                </PrivateRoute>
              }
            />
            <Route
              path="diretoria-rede"
              element={
                <PrivateRoute redirectTo="/login">
                  <FluxoNormal />
                </PrivateRoute>
              }
            >
              <Route
                path=":id"
                element={
                  <PrivateRoute redirectTo="/login">
                    <MissaoDestaqueCreate />
                  </PrivateRoute>
                }
              />
            </Route>
            <Route
              path="mad"
              element={
                <PrivateRoute redirectTo="/login">
                  <FluxoMAD />
                </PrivateRoute>
              }
            >
              <Route
                path=":id"
                element={
                  <PrivateRoute redirectTo="/login">
                    <MissaoDestaqueCreate />
                  </PrivateRoute>
                }
              />
            </Route>
          </Route>

          <Route
            path="participantes-da-realizacao"
            element={
              <PrivateRoute redirectTo="/login">
                <AnalisarParticipacaoEquipe />
              </PrivateRoute>
            }
          />

          {/* <Route
            path="dupla-lideranca"
            element={
              <PrivateRoute redirectTo="/login">
                <PontuamMasNaoAvaliam />
              </PrivateRoute>
            }
          /> */}

          {/* <Route
            path="participacao-de-gerentes"
            element={
              <PrivateRoute redirectTo="/login">
                <AnalisarParticipacaoGerentes />
              </PrivateRoute>
            }
          /> */}
        </Route>

        <Route
          path="/rede-joga-junto"
          element={
            <PrivateRoute redirectTo="/login">
              <Rede />
            </PrivateRoute>
          }
        />

        {/* Irmãs da rota do Feed, e não filhas: o Feed não as desenha junto da lista (AJ3-01, AJ3-09). */}
        <Route
          path={ROTA_DOS_RECONHECIMENTOS_DO_PREFIXO}
          element={
            <PrivateRoute redirectTo="/login">
              <ReconhecimentosDoPrefixo />
            </PrivateRoute>
          }
        />

        <Route
          path={ROTA_DAS_REALIZACOES_DO_PREFIXO}
          element={
            <PrivateRoute redirectTo="/login">
              <RealizacoesDoPrefixo />
            </PrivateRoute>
          }
        />

        {/* <Route
          path="/historico"
          element={
            <PrivateRoute redirectTo="/login">
              <Historico />
            </PrivateRoute>
          }
        /> */}

        <Route
          path="/manifesto"
          element={
            <PrivateRoute redirectTo="/login">
              <Manifesto />
            </PrivateRoute>
          }
        />

        <Route
          path="/experiencias"
          element={
            <PrivateRoute redirectTo="/login">
              <Catalog />
            </PrivateRoute>
          }
        >
          <Route path=":id" element={<ShopDetail />} />
        </Route>

        <Route
          path="/galeria"
          element={
            <PrivateRoute redirectTo="/login">
              <GaleriaReconhecimento />
            </PrivateRoute>
          }
        ></Route>

        <Route
          path="/destaques-cultura"
          element={
            <PrivateRoute redirectTo="/login">
              <DestaquesCultura />
            </PrivateRoute>
          }
        ></Route>

        <Route
          path="/catalogo-de-experiencias"
          element={
            <PrivateRoute redirectTo="/login">
              <Catalogo />
            </PrivateRoute>
          }
        ></Route>

        <Route
          path="/catalogo-de-experiencias-list"
          element={
            <PrivateRoute redirectTo="/login">
              <ExperienceCatalog />
            </PrivateRoute>
          }
        ></Route>

        <Route
          path="catalogo-de-experiencias-list/feedback"
          element={
            <PrivateRoute redirectTo="/login">
              <ExperienceCatalogFeedbackPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/catalogo"
          element={
            <PrivateRoute redirectTo="/login">
              <Catalog />
            </PrivateRoute>
          }
        ></Route>

        <Route
          path="/destaques"
          element={
            <PrivateRoute redirectTo="/login">
              <Destaque />
            </PrivateRoute>
          }
        ></Route>

        <Route
          path="/desafios/:id"
          element={
            <PrivateRoute redirectTo="/login">
              <Challenges />
            </PrivateRoute>
          }
        ></Route>

        <Route
          path="/desafios-lancamento"
          element={
            <PrivateRoute redirectTo="/login">
              <ChallengesLancamento />
            </PrivateRoute>
          }
        ></Route>

        <Route
          path="/regulamento"
          element={
            <PrivateRoute redirectTo="/login">
              <Regulamento />
            </PrivateRoute>
          }
        ></Route>

        <Route
          path="/selos"
          element={
            <PrivateRoute redirectTo="/login">
              <Selos />
            </PrivateRoute>
          }
        ></Route>

        <Route
          path="/loja"
          element={
            <PrivateRoute redirectTo="/login">
              <Store />
            </PrivateRoute>
          }
        ></Route>

        <Route path="/votacao" element={<Votacao />}></Route>

        <Route
          path="/ajuda"
          element={
            <PrivateRoute redirectTo="/login">
              <Faq />
            </PrivateRoute>
          }
        />

        {/* MINHA PREFERENCIAS */}

        <Route
          path="/minhas-preferencias"
          element={
            <PrivateRoute redirectTo="/login">
              <Preference />
            </PrivateRoute>
          }
        />

        {/* As notificações abrem num painel lateral da Home; o endereço antigo da Central leva para lá. */}
        <Route path="/notificacoes" element={<Navigate to="/" replace />} />

        <Route
          path="/teste-sharepoint"
          element={
            <PrivateRoute redirectTo="/login">
              <TesteSharePoint />
            </PrivateRoute>
          }
        />
        </Route>

        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />

        {/* Sem PublicRoute (que manda o usuário logado para "/") nem
            PrivateRoute: a vitrine abre logado ou não, sem o menu do app. */}
        {VitrineComponentes && (
          <Route
            path="/vitrine-componentes"
            element={
              <Suspense fallback={null}>
                <VitrineComponentes />
              </Suspense>
            }
          />
        )}

        <Route path="*" element={<NotFound />} />
      </Routes>
    </MsalProvider>
  );
};
